import { useState } from 'react'

import { Box, Text, WizardStepRenderParams, useOnNext } from '@factorialco/deprecated-design-system'
import {
  SelectDataStepDocument,
  UpdatePayrollSummaryImportDocument,
} from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import Loading from 'modules/layout/components/Page/Loading'
import { WizardData } from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/ImportSummaryWizard/types'
import WorksheetTable from 'modules/payroll_bundle/components/common/components/WorksheetTable'
import { ResultMatches } from 'modules/payroll_bundle/components/common/helper/worksheet'
import { MATCH_MAX_ROWS } from 'modules/payroll_bundle/lib/constants'
import {
  RESULT_CONFIG_BUTTONS,
  WorksheetButton,
  WorksheetMatches,
  convertConfigButtonsToWorksheetButtons,
} from 'modules/payroll_bundle/lib/importConfig'
import {
  activeButtonLabel,
  areAllFieldsSelected,
  isButtonRequired,
} from 'modules/payroll_bundle/lib/importConfig'
import buildImportConfig from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/Header/buildImportConfig'
import WorksheetHeader from 'modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/WorksheetHeader'

import ImportSummaryNavigationTabs from '../_common/header'

const SelectDataStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [activeButton, setActiveButton] = useState<string>('name')
  const worksheetData = JSON.parse(data.payrollSummaryImport.worksheetData || '{}')
  const [worksheetMatches, setWorksheetMatches] = useState<ResultMatches>(
    data.worksheetMatches || {}
  )
  const [visibleButtons, setVisibleButtons] = useState<WorksheetButton[]>(
    convertConfigButtonsToWorksheetButtons(RESULT_CONFIG_BUTTONS.sort((a, b) => a.order - b.order))
  )

  const [updatePayrollSummaryConfig] = useMutation(UpdatePayrollSummaryImportDocument, {
    extractorFn: (data) => data.payrollMutations.updatePayrollPayrollSummaryImport,
  })

  const { data: selectDateStep, loading: loadingData } = useQuery(SelectDataStepDocument, {
    variables: {
      payrollSummaryId: Number(data.payrollSummaryImport.id),
      policyId: Number(data.payrollPolicy.id),
    },
  })

  const worksheetSuggestion = selectDateStep?.payroll.payrollSummaryCellSuggestionsConnection.nodes

  useUnsafeEffect(() => {
    const cellMatches: WorksheetMatches = {}

    worksheetSuggestion?.map((cell) => {
      const { cellIdentifier, row, column, value } = cell
      // README: The row and column indexes are 0-based
      // but they are stored as 1-base, so we need to subtract 1 to match the indexes
      cellMatches[cellIdentifier] = [row - 1, column - 1, value]
    })

    setWorksheetMatches({ ...cellMatches })

    setActiveButton(Object.keys({ ...cellMatches }).length > 0 ? '' : 'name')
    onDataChanged({ ...data, worksheetMatches: { ...cellMatches } })
  }, [worksheetSuggestion])

  const showDataTrimmingNotice =
    worksheetData.length > MATCH_MAX_ROWS ||
    (worksheetData[0] && worksheetData[0].length > MATCH_MAX_ROWS)

  const onWorksheetMatchesChange = (newWorksheetMatches: ResultMatches) => {
    setWorksheetMatches(newWorksheetMatches)
    onDataChanged({ ...data, worksheetMatches: newWorksheetMatches })
  }

  const onClickButton = (button: string) => {
    setActiveButton(button)
  }

  const getTitle = () => {
    if (isButtonRequired(activeButton)) {
      return i18n.t(
        'payroll_bundle.payroll_summary_import.wizard.select_data_step.mandatory_no_done',
        {
          field: activeButtonLabel(activeButton),
        }
      )
    }

    return areAllFieldsSelected(worksheetMatches)
      ? i18n.t('payroll_bundle.payroll_summary_import.wizard.select_data_step.done')
      : i18n.t('payroll_bundle.payroll_summary_import.wizard.select_data_step.mandatory_done')
  }

  const handleNext = async () => {
    if (data.worksheetMatches === undefined) return

    const importConfig = buildImportConfig({
      payrollImportId: data.payrollSummaryImport.id,
      worksheetMatches: data.worksheetMatches,
    })

    try {
      const result = await updatePayrollSummaryConfig({
        variables: {
          companyIdentifierCell: importConfig.companyIdentifierCell,
          firstNameCell: importConfig.firstNameCell,
          idCell: importConfig.idCell,
          importId: importConfig.importId,
          lastNameCell: importConfig.lastNameCell,
          netSalaryCell: importConfig.netSalaryCell,
        },
      })

      const handle = handleResult(result, () => {})
      if (handle.success && handle.data?.payrollSummaryImport) {
        data?.onUpdateImportSummary?.({
          ...data.payrollSummaryImport,
          ...handle.data.payrollSummaryImport,
        })
      }
    } catch (err) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
      return Promise.reject()
    }

    track(trackingEvents.payroll.continueToMapDataToConceptStep, {
      period_status: data.payrollPolicyPeriod.status,
      mapped_column_items: Object.keys(data.worksheetMatches),
      source: data.source,
    })
  }

  useOnNext(handleNext)

  if (loadingData) return <Loading></Loading>

  return (
    <>
      <Box gap="s24" overflowY="hidden">
        <ImportSummaryNavigationTabs selectedStep={0} />
        <Text.Normal>{getTitle()}</Text.Normal>
        <Box gap="s0" overflowX="auto">
          <WorksheetTable
            padding="s1"
            activeButton={activeButton}
            worksheetData={worksheetData}
            worksheetMatches={worksheetMatches}
            allowZeroValues
            sequentialButtons={RESULT_CONFIG_BUTTONS}
            onClickButton={onClickButton}
            onWorksheetMatchesChange={onWorksheetMatchesChange}
            header={
              <WorksheetHeader
                activeButton={activeButton}
                visibleButtons={visibleButtons}
                setVisibleButtons={setVisibleButtons}
                worksheetData={worksheetData}
                worksheetMatches={worksheetMatches}
                showDataTrimmingNotice={showDataTrimmingNotice}
                onClickButton={onClickButton}
                onWorksheetMatchesChange={onWorksheetMatchesChange}
              />
            }
          />
        </Box>
      </Box>
    </>
  )
}

export default SelectDataStep
